import React, { useState, useRef, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Menu,
  FileText,
  User,
  Target,
  Copy,
  Shield,
  Scale,
  AlertCircle,
  X,
  ChevronLeft,
  Download,
  Handshake,
  SquareSigma,
  Navigation,
  LayoutTemplate,
  ScanSearch,
  MessageSquare,
} from "lucide-react";
import {
  Box,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
  useTheme,
  useMediaQuery,
  Container,
  Stack,
  AppBar,
  Toolbar,
  Button,
  CircularProgress,
} from "@mui/material";
import WelcomePage from "./reportSections/welcomePage";
import Forward from "./reportSections/forward";
import ReportGrade from "./reportSections/reportGrade";
import Category from "./reportSections/category";
import PerfoSec from "./reportSections/perfoSec";
import PageSpeed from "./reportSections/pageSpeed";
import SeoMetrics from "./reportSections/seoMetrics";
import ViewportAnalysis from "./reportSections/viewportAnalysis";
import AuditClosure from "./reportSections/auditClosure";
import ScheduleConsultation from "./reportSections/scheduleConsultation";
import axios from "axios";
import config from "../config.json";
import { useNavigate } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import MilkTubLogo from "./utils/images/milktub-logo.svg";
import "./report.css";
import { hover } from "@testing-library/user-event/dist/cjs/convenience/hover.js";

const Report = () => {
  const { reportID } = useParams();
  const navigate = useNavigate();

  const [reportData, setReportData] = useState(null);

  const contentRef = useRef(null);
  const reactToPrintFn = useReactToPrint({
    contentRef,
    documentTitle: "Report.pdf",
    copyShadowRoots: true,
  });

  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("lg"), {
    noSsr: true,
    defaultMatches: true,
  });

  const isMobileScreen = useMediaQuery(theme.breakpoints.down("sm"), {
    noSsr: true,
    defaultMatches: true,
  });

  const [isNavOpen, setIsNavOpen] = useState(false);

  useEffect(() => {
    axios
      .get(`${config.apiBaseUrl}/reports/find?report_id=` + reportID)
      .then((response) => {
        console.log(response.data);
        localStorage.setItem("reportID", reportID);
        localStorage.setItem("reportData", JSON.stringify(response.data));
        setReportData(response.data);
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
        setReportData("error");
      });
  }, [reportID]);

  if (!reportData) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          flexDirection: "column",
        }}
      >
        <CircularProgress size={"5rem"} />
        <Typography variant="body2" color="primary" sx={{ mt: 2 }}>
          Loading report data...
        </Typography>
      </Box>
    );
  }

  if (!reportData.report) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          flexDirection: "column",
        }}
      >
        <Typography variant="h4" color="error" sx={{ mb: 2 }}>
          Report not found
        </Typography>
        <Typography
          variant="body2"
          textAlign={"center"}
          color="textSecondary"
          sx={{ mt: 2 }}
        >
          Please check the URL or contact admin.
        </Typography>
      </Box>
    );
  }

  if (reportData === "error" || reportData.report?.status === "fail") {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          flexDirection: "column",
        }}
      >
        <Typography variant="h4" color="error" sx={{ mb: 2 }}>
          Error fetching report data
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => window.location.reload()}
        >
          Retry
        </Button>
        <Typography
          variant="body2"
          textAlign={"center"}
          color="textSecondary"
          sx={{ mt: 2 }}
        >
          {reportData.report?.status === "fail" &&
            "Contact admin if this issue persists."}
        </Typography>
      </Box>
    );
  }

  const categories = reportData.report.report_json.report.categories;

  const getCategoryIcon = (id) => {
    switch (id) {
      case "welcomePage":
        return <Handshake />;
      case "forewordPage":
      case "auditClosure":
        return <FileText />;
      case "reportGrade":
        return <SquareSigma />;
      case "sectionAnalysis":
        return <LayoutTemplate />;
      case "user_experience":
        return <User />;
      case "conversion_optimization":
        return <Target />;
      case "content_copy":
        return <Copy />;
      case "legal":
        return <Scale />;
      case "navigation":
        return <Navigation />;
      case "performance_and_security":
        return <Shield />;
      case "seoMetrics":
        return <ScanSearch />;
      case "nextSteps":
        return <AlertCircle />;
      case "downloadPDF":
        return <Download />;
      case "scheduleConsultation":
        return <MessageSquare />;
      default:
        return <User />;
    }
  };

  const cateNav = categories.map((category) => ({
    id: category.id,
    title: category.category_name,
    icon: getCategoryIcon(category.id),
    action: () => scrollIntoView(category.id),
  }));

  const metricIdMap = {};
  categories.forEach((category) => {
    metricIdMap[category.category_name] = category.id;
  });
  metricIdMap["Performance and Security"] = "performance_and_security";
  metricIdMap["SEO and Online Visibility"] = "seoMetrics";

  const scrollIntoView = (id) => {
    document.querySelector(`#${id}`).scrollIntoView({
      behavior: "smooth",
    });
  };

  const sections = [
    {
      id: "downloadPDF",
      title: "Download PDF",
      icon: getCategoryIcon("downloadPDF"),
      action: () => {
        reactToPrintFn();
      },
    },
    {
      id: "welcomePage",
      title: "Welcome",
      icon: getCategoryIcon("welcomePage"),
      action: () => scrollIntoView("welcomePage"),
    },
    {
      id: "forewordPage",
      title: "Audit Foreword",
      icon: getCategoryIcon("forewordPage"),
      action: () => scrollIntoView("forewordPage"),
    },
    {
      id: "reportGrade",
      title: "Report Grade",
      icon: <SquareSigma />,
      action: () => scrollIntoView("reportGrade"),
    },
    {
      id: "sectionAnalysis",
      title: "Section Analysis",
      icon: getCategoryIcon("sectionAnalysis"),
      action: () => scrollIntoView("sectionAnalysis"),
    },
    ...cateNav,
    {
      id: "performance_and_security",
      title: "Performance & Security",
      icon: getCategoryIcon("performance_and_security"),
      action: () => scrollIntoView("performance_and_security"),
    },
    {
      id: "seoMetrics",
      title: "SEO and Online Visibility",
      icon: getCategoryIcon("seoMetrics"),
      action: () => scrollIntoView("seoMetrics"),
    },
    {
      id: "auditClosure",
      title: "Audit Closure",
      icon: getCategoryIcon("auditClosure"),
      action: () => scrollIntoView("auditClosure"),
    },
    {
      id: "scheduleConsultation",
      title: "Schedule Consultation",
      icon: getCategoryIcon("scheduleConsultation"),
      action: () => scrollIntoView("scheduleConsultation"),
      styles: {
        bgcolor: "rgb(193, 216, 47)",
        color: "white",
        "&:hover": { bgcolor: "rgba(193, 216, 47,0.8)" },
      },
      iconColor: "white",
    },
  ].filter((section) => !isMobileScreen || section.id !== "downloadPDF");

  const drawerWidth = 280;

  const drawer = (
    <Box>
      <AppBar
        position="static"
        color="inherit"
        elevation={0}
        sx={{
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Toolbar>
          <Box
            display="flex"
            alignItems="center"
            gap={1}
            sx={{ cursor: "pointer", padding: 1, marginTop: 1 }}
            onClick={() => (window.location.href = "/")}
          >
            <ChevronLeft sx={{ cursor: "pointer" }} />
            <img
              src={MilkTubLogo}
              alt="Milk Tub Logo"
              style={{ width: "80%", height: "auto" }}
            />
          </Box>
        </Toolbar>
      </AppBar>
      <List>
        {sections.map((section) => (
          <ListItemButton
            key={section.id}
            onClick={(e) => {
              e.preventDefault();
              if ("action" in section) section.action();
              if (!isLargeScreen) setIsNavOpen(false);
            }}
            sx={section.styles}
          >
            <ListItemIcon sx={{ minWidth: 40, color: section.iconColor }}>
              {section.icon}
            </ListItemIcon>
            <ListItemText primary={section.title} />
          </ListItemButton>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "grey.100" }}>
      {!isLargeScreen && (
        <IconButton
          color="inherit"
          aria-label="open drawer"
          edge="start"
          onClick={() => setIsNavOpen(!isNavOpen)}
          sx={{
            position: "fixed",
            top: 16,
            left: 16,
            zIndex: 1300,
            bgcolor: "background.paper",
            boxShadow: 1,
          }}
        >
          {isNavOpen ? <X /> : <Menu />}
        </IconButton>
      )}

      <Box
        component="nav"
        sx={{
          width: { lg: drawerWidth },
          flexShrink: { lg: 0 },
        }}
      >
        {isLargeScreen ? (
          <Box
            sx={{
              width: drawerWidth,
              height: "100vh",
              borderRight: 1,
              borderColor: "divider",
              bgcolor: "background.paper",
            }}
          >
            {drawer}
          </Box>
        ) : (
          <Drawer
            variant="temporary"
            open={isNavOpen}
            onClose={() => setIsNavOpen(false)}
            ModalProps={{ keepMounted: true }}
            sx={{
              "& .MuiDrawer-paper": {
                width: drawerWidth,
                bgcolor: "background.paper",
              },
            }}
          >
            {drawer}
          </Drawer>
        )}
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          height: "100vh",
          overflow: "auto",
          py: 3,
        }}
      >
        <Container id="reportPDF" maxWidth="md" ref={contentRef}>
          <Stack spacing={0}>
            <Paper
              id="welcomePage"
              elevation={1}
              square={true}
              sx={{
                width: "100%",
                maxWidth: "100%",
                height: "auto",
              }}
            >
              <WelcomePage
                images={{
                  desktop:
                    reportData.report.report_json.first_viewport_image_url,
                  mobile: reportData.report.report_json.mobile_image_url,
                }}
                domain={reportData.report.domain}
                description={
                  reportData.customization?.custom_data.welcome_page_description
                    .original || ""
                }
                generatedBy={
                  reportData.customization?.custom_data.generated_by || {}
                }
              />
            </Paper>
            <br className="no-print" />
            <Paper
              id="forewordPage"
              elevation={1}
              square={true}
              sx={{
                width: "100%",
                maxWidth: "100%",
                height: "auto",
              }}
            >
              <Forward
                introText={
                  reportData.customization?.custom_data?.audit_intro?.original
                }
              />
            </Paper>
            <br className="no-print" />
            <Paper
              elevation={1}
              square={true}
              sx={{
                width: "100%",
                maxWidth: "100%",
                height: "auto",
              }}
              id="reportGrade"
            >
              <ReportGrade
                domain={reportData.report.domain}
                metrics={reportData.report.report_json.report.category_scores}
                score={reportData.report.report_json.report.report_score}
                summary={reportData.report.report_json.report.summary}
                metricIdMap={metricIdMap}
                scrollIntoView={scrollIntoView}
              />
            </Paper>
            <Paper
              elevation={1}
              square={true}
              sx={{
                width: "100%",
                maxWidth: "100%",
                height: "auto",
              }}
              id="sectionAnalysis"
            >
              <Typography
                variant="h3"
                component="h1"
                textAlign={{ xs: "center", sm: "left" }}
                sx={{
                  my: 4,
                  px: 3,
                  textAlign: "center",
                }}
                fontWeight="bold"
                gutterBottom
              >
                Section Analysis
              </Typography>
              {reportData.report.report_json.report.viewports.map(
                (viewport) => (
                  <ViewportAnalysis data={viewport} />
                )
              )}
            </Paper>
            {categories.map((category) => (
              <Paper
                elevation={1}
                square={true}
                sx={{
                  width: "100%",
                  maxWidth: "100%",
                  height: "auto",
                }}
                id={category.id}
                className="Category"
              >
                <Category category={category} />
              </Paper>
            ))}
            <Paper
              elevation={1}
              square={true}
              sx={{
                width: "100%",
                maxWidth: "100%",
                height: "auto",
              }}
              id="performance_and_security"
            >
              <PerfoSec
                data={
                  reportData.report.report_json.report
                    .performance_and_security[0]
                }
              />
              <PageSpeed
                data={reportData.report.report_json.report.pagespeed[0]}
              />
            </Paper>
            <Paper
              elevation={1}
              square={true}
              sx={{
                width: "100%",
                maxWidth: "100%",
                height: "auto",
              }}
              id="seoMetrics"
            >
              <SeoMetrics
                data={reportData.report.report_json.report.seo_audit[0]}
              />
            </Paper>
            <br className="no-print" />
            <Paper
              elevation={1}
              square={true}
              sx={{
                width: "100%",
                maxWidth: "100%",
                height: "auto",
              }}
              id="auditClosure"
            >
              <AuditClosure
                closureText={
                  reportData.customization?.custom_data?.audit_closure?.original
                }
              />
            </Paper>
            <br className="no-print" />
            <Paper
              elevation={1}
              square={true}
              sx={{
                width: "100%",
                maxWidth: "100%",
                height: "auto",
              }}
              id="scheduleConsultation"
            >
              <ScheduleConsultation
                consultationConfig={
                  reportData.customization?.custom_data?.book_appointment
                }
                contact={reportData.customization?.custom_data?.generated_by}
              />
            </Paper>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
};

export default Report;
